import { useSyncExternalStore } from 'react';

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

interface BreakpointState {
  /** < 768px */
  isMobile: boolean;
  /** 768px - 1023px */
  isTablet: boolean;
  /** >= 1024px */
  isDesktop: boolean;
  /** < 640px */
  isSm: boolean;
  /** 640px - 767px */
  isMd: boolean;
  /** 1024px - 1279px */
  isLg: boolean;
  /** >= 1280px */
  isXl: boolean;
  /** Current viewport width */
  width: number;
  /** Current named breakpoint */
  current: Breakpoint;
}

const queries = {
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1023px)',
  desktop: '(min-width: 1024px)',
  sm: '(max-width: 639px)',
  md: '(min-width: 640px) and (max-width: 767px)',
  lg: '(min-width: 1024px) and (max-width: 1279px)',
  xl: '(min-width: 1280px)',
} as const;

let cachedState: BreakpointState | null = null;
const listeners = new Set<() => void>();

function computeState(): BreakpointState {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isSm: false,
      isMd: false,
      isLg: false,
      isXl: true,
      width: 1280,
      current: 'xl',
    };
  }

  const isMobile = window.matchMedia(queries.mobile).matches;
  const isTablet = window.matchMedia(queries.tablet).matches;
  const isDesktop = window.matchMedia(queries.desktop).matches;
  const isSm = window.matchMedia(queries.sm).matches;
  const isMd = window.matchMedia(queries.md).matches;
  const isLg = window.matchMedia(queries.lg).matches;
  const isXl = window.matchMedia(queries.xl).matches;
  const width = window.innerWidth;

  let current: Breakpoint = 'xl';
  if (isSm) current = 'sm';
  else if (isMd) current = 'md';
  else if (isLg) current = 'lg';
  else current = 'xl';

  return { isMobile, isTablet, isDesktop, isSm, isMd, isLg, isXl, width, current };
}

function getSnapshot(): BreakpointState {
  if (!cachedState) {
    cachedState = computeState();
  }
  return cachedState;
}

function getServerSnapshot(): BreakpointState {
  return {
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isSm: false,
    isMd: false,
    isLg: false,
    isXl: true,
    width: 1280,
    current: 'xl',
  };
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);

  // Set up matchMedia listeners on first subscribe
  if (listeners.size === 1 && typeof window !== 'undefined') {
    const handler = () => {
      const newState = computeState();
      const prev = cachedState;
      if (
        !prev ||
        prev.isMobile !== newState.isMobile ||
        prev.isTablet !== newState.isTablet ||
        prev.isDesktop !== newState.isDesktop ||
        prev.width !== newState.width
      ) {
        cachedState = newState;
        listeners.forEach((fn) => fn());
      }
    };

    const mediaQueryLists = Object.values(queries).map((q) => window.matchMedia(q));
    mediaQueryLists.forEach((mql) => mql.addEventListener('change', handler));

    // Store cleanup for the "last unsubscribe" case
    (subscribe as any)._cleanup = () => {
      mediaQueryLists.forEach((mql) => mql.removeEventListener('change', handler));
    };
  }

  return () => {
    listeners.delete(callback);
    if (listeners.size === 0 && (subscribe as any)._cleanup) {
      (subscribe as any)._cleanup();
      (subscribe as any)._cleanup = null;
      cachedState = null;
    }
  };
}

export function useBreakpoint(): BreakpointState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
